import React, { useState } from "react";
import { toast } from "sonner";
import { postLogin, friendlyLoginError } from "../utils/loginRequest";

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [secret, setSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password || !secret) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      const response = await postLogin(
        { email, password, secret },
        () => toast.message("Connecting to server… retrying"),
      );

      if (response.data.success) {
        // Store token in localStorage
        localStorage.setItem("auth_token", response.data.token);
        localStorage.setItem("user_email", email);
        
        // Get user role from backend response or set to admin for Kaleb's email
        const userRole = response.data.role || (email === 'kalebarancelovic@outlook.com' ? 'admin' : 'user');
        localStorage.setItem("user_role", userRole);
        
        toast.success("Login successful!");
        onLoginSuccess(response.data.token);
      } else {
        toast.error(response.data.message || "Invalid credentials");
      }
    } catch (err) {
      console.error("Login error:", err);
      toast.error(friendlyLoginError(err, "Login failed. Please check your credentials."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #dbeafe 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "2rem"
    }}>
      <div style={{
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(8, 145, 178, 0.2)",
        borderRadius: "24px",
        padding: "3rem",
        maxWidth: "450px",
        width: "100%",
        boxShadow: "0 20px 60px rgba(8, 145, 178, 0.15)"
      }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <h1 style={{
            fontSize: "2.5rem",
            fontWeight: "700",
            background: "linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #3b82f6 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            marginBottom: "0.5rem",
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
            Trade Blindfolded
          </h1>
          <p style={{ color: "#64748b", fontSize: "0.95rem" }}>
            Please login to access your trading dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: "600",
              color: "#334155",
              marginBottom: "0.5rem"
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              style={{
                width: "100%",
                padding: "0.875rem",
                background: "rgba(255, 255, 255, 0.8)",
                border: "2px solid rgba(8, 145, 178, 0.3)",
                borderRadius: "8px",
                fontSize: "1rem",
                color: "#0f172a",
                outline: "none",
                transition: "all 0.3s ease"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#0891b2";
                e.target.style.boxShadow = "0 0 0 3px rgba(8, 145, 178, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "rgba(8, 145, 178, 0.3)";
                e.target.style.boxShadow = "none";
              }}
              data-testid="email-input"
            />
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: "600",
              color: "#334155",
              marginBottom: "0.5rem"
            }}>
              Password
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                style={{
                  width: "100%",
                  padding: "0.875rem",
                  paddingRight: "3rem",
                  background: "rgba(255, 255, 255, 0.8)",
                  border: "2px solid rgba(8, 145, 178, 0.3)",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  color: "#0f172a",
                  outline: "none",
                  transition: "all 0.3s ease"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#0891b2";
                  e.target.style.boxShadow = "0 0 0 3px rgba(8, 145, 178, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(8, 145, 178, 0.3)";
                  e.target.style.boxShadow = "none";
                }}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "0.875rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "1.25rem",
                  color: "#64748b",
                  padding: "0.25rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "color 0.2s ease"
                }}
                onMouseEnter={(e) => e.target.style.color = "#0891b2"}
                onMouseLeave={(e) => e.target.style.color = "#64748b"}
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <label style={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: "600",
              color: "#334155",
              marginBottom: "0.5rem"
            }}>
              Secret Word
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showSecret ? "text" : "password"}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="Enter your secret word"
                style={{
                  width: "100%",
                  padding: "0.875rem",
                  paddingRight: "3rem",
                  background: "rgba(255, 255, 255, 0.8)",
                  border: "2px solid rgba(8, 145, 178, 0.3)",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  color: "#0f172a",
                  outline: "none",
                  transition: "all 0.3s ease"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#0891b2";
                  e.target.style.boxShadow = "0 0 0 3px rgba(8, 145, 178, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(8, 145, 178, 0.3)";
                  e.target.style.boxShadow = "none";
                }}
                data-testid="secret-input"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                style={{
                  position: "absolute",
                  right: "0.875rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "1.25rem",
                  color: "#64748b",
                  padding: "0.25rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "color 0.2s ease"
                }}
                onMouseEnter={(e) => e.target.style.color = "#0891b2"}
                onMouseLeave={(e) => e.target.style.color = "#64748b"}
              >
                {showSecret ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "1rem",
              background: loading ? "rgba(148, 163, 184, 0.5)" : "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)",
              border: "none",
              borderRadius: "12px",
              color: "white",
              fontSize: "1.125rem",
              fontWeight: "700",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              fontFamily: "'Space Grotesk', sans-serif",
              boxShadow: loading ? "none" : "0 10px 30px rgba(8, 145, 178, 0.3)"
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 15px 40px rgba(8, 145, 178, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = loading ? "none" : "0 10px 30px rgba(8, 145, 178, 0.3)";
            }}
            data-testid="login-button"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        <div style={{
          marginTop: "2rem",
          padding: "1rem",
          background: "rgba(8, 145, 178, 0.05)",
          borderRadius: "8px",
          border: "1px solid rgba(8, 145, 178, 0.2)"
        }}>
          <p style={{ fontSize: "0.75rem", color: "#64748b", textAlign: "center", margin: 0 }}>
            🔒 Secure access - Your credentials are protected with encryption
          </p>
        </div>

        {/* Forgot Password Link - Note: Will cause full page reload but necessary for public route access */}
        <div style={{ marginTop: "1rem", textAlign: "center" }}>
          <span
            style={{
              fontSize: "0.875rem",
              color: "#94a3b8",
              fontStyle: "italic"
            }}
          >
            🔑 Forgot Password? (Coming soon)
          </span>
        </div>

        {/* Request Invite Button - Note: Will cause full page reload but necessary for public route access */}
        <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
          <p style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "0.75rem" }}>
            Don't have an account?
          </p>
          <span
            style={{
              display: "inline-block",
              padding: "0.875rem 2rem",
              background: "linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)",
              color: "white",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "0.875rem",
              border: "2px solid rgba(148, 163, 184, 0.3)"
            }}
          >
            📨 Request Invite (Coming soon)
          </span>
        </div>
      </div>
    </div>
  );
};

export default Login;
